import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiManualReviewData,
  type EnrollmentMultiManualReviewSubmitInput,
  type EnrollmentMultiManualReviewSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi ManualReview data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiManualReviewData>
 * @example const response = await getEnrollmentMultiManualReviewData()
 */
export const getEnrollmentMultiManualReviewData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiManualReviewData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiManualReviewData',
      query: `mutation getEnrollmentMultiManualReviewData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiManualReviewData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiManualReviewData = createQueryHook<undefined, EnrollmentMultiManualReviewData>(
  getEnrollmentMultiManualReviewData,
)

/**
 * Submit Enrollment Multi ManualReview data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiManualReviewSubmitOutput>
 * @example const response = await submitEnrollmentMultiManualReviewData();
 */
export const submitEnrollmentMultiManualReviewData = async (
  enrollmentManualReviewSubmitInput: EnrollmentMultiManualReviewSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiManualReviewSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiManualReviewData',
      query: `mutation submitEnrollmentMultiManualReviewData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentManualReviewSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiManualReviewSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiManualReviewData = createMutationHook<
  EnrollmentMultiManualReviewSubmitInput,
  EnrollmentMultiManualReviewSubmitOutput
>(submitEnrollmentMultiManualReviewData)
